import invariant from 'react-utils/invariant';
import CartographerReporter, { CartographerEndpoint } from './CartographerReporter';
import { DEFAULT_TIMEOUT } from '../Constants';
import { onVisibilityChange, visibilityState, wasHiddenOnScriptStart } from '../visibility';
import { PageLoadMetrics } from '../Metrics';
import { isReload, performanceGetEntriesByType } from '../navigation';
import { getHubHttpData } from '../httpRequestsStats';
import { getIsAiCopilotEnabled } from '../aiCopilot';
import Raven from 'raven-js';
import { cleanseRoute } from '../cleanseRoute';
import { isPrerendering } from '../env';
import { maybeGetReferrerApp } from '../appTransitionHistory';
const STATIC_DOMAIN_REGEX = /https:\/\/(static|local).hsappstatic.net\//;

// If the duration of the request is <= 10ms mark the request as a cache hit.
const CACHE_DURATION_MS = 10;
export default class ReaganCompatReporter extends CartographerReporter {
  constructor(options) {
    super(options);
    this.abandonedTimes = [];
    this.lastAbandonedTimestamp = null;
    this.finished = false;
    this.wasHidden = visibilityState() === 'hidden';
    this.isAbandoned = false;
    this.prerendering = isPrerendering();
    this.finishedTimestamp = null;
    this.finishedStatus = null;
    this.reportedMarkers = new Set();
    this.currentRoute = null;
    // These flags measure if the page was hidden within 500ms (user switched to another tab or window)
    // and remained invisible. This is used to filter out false failures when the page was hidden before
    // the user was frustrated, because the tab becomes throttled and loads very slowly.
    this.hiddenEntirelyBeforeRhumbFinished = wasHiddenOnScriptStart && visibilityState() === 'hidden';
    this.hiddenWithin1000ms = visibilityState() === 'hidden';
    this.hiddenWithin500ms = visibilityState() === 'hidden';
    this.setCustomAttribute('currentVisibility', visibilityState());
    this.setCustomAttribute('visibility', visibilityState());
    let becameVisibleAgain = false;
    onVisibilityChange(state => {
      this.setCustomAttribute('currentVisibility', state);
      if (this.hiddenEntirelyBeforeRhumbFinished && state === 'visible') {
        this.hiddenEntirelyBeforeRhumbFinished = false;
      }
      if (this.wasHidden && state === 'visible') {
        becameVisibleAgain = true;
        this.hiddenWithin1000ms = false;
        this.hiddenWithin500ms = false;
      }
      if (state === 'hidden' && !this.wasHidden) {
        this.wasHidden = true;
        this.setCustomAttribute('visibility', 'hidden');
        if (!becameVisibleAgain) {
          const now = performance.now();
          if (now <= 1000) {
            this.hiddenWithin1000ms = true;
          }
          if (now <= 500) {
            this.hiddenWithin500ms = true;
          }
        }
      }
    });
  }
  getCacheStatusData() {
    const cacheStatusData = {};
    this.performanceEntries().forEach(timing => {
      if (timing.name.endsWith('.js')) {
        const fileName = timing.name.replace(STATIC_DOMAIN_REGEX, '');
        cacheStatusData[fileName] = {
          cached: timing.duration <= CACHE_DURATION_MS,
          duration: timing.duration
        };
      }
    });
    return cacheStatusData;
  }
  incrementTimeoutCounters(route, isAiCopilotEnabled, referrerApp) {
    const metadata = Object.assign({
      route: cleanseRoute(route),
      isAiCopilotEnabled
    }, referrerApp ? {
      referrerApp
    } : {});
    if (!this.wasHidden) {
      PageLoadMetrics.counter('timeouts', metadata).increment();
    }
    if (!this.hiddenWithin1000ms) {
      PageLoadMetrics.counter('timeouts_1000ms_visiblity_threshold', metadata).increment();
    }
    if (!this.hiddenWithin500ms) {
      PageLoadMetrics.counter('timeouts_500ms_visiblity_threshold', metadata).increment();
    }
    if (!this.hiddenEntirelyBeforeRhumbFinished) {
      PageLoadMetrics.counter('timeouts_entirely_hidden_filtered_out', metadata).increment();
    }
  }
  reportTimeoutForRaceCondition(route, duration, timestamp, totalTimeFromRouteStart, checks, referrerApp, isAiCopilotEnabled, extraContext) {
    // Log detection of race condition for monitoring
    Raven.capturePageEvent('rhumbTimeoutRaceConditionDetected', {
      extra: Object.assign({
        duration,
        totalTimeFromRouteStart,
        totalDuration: duration + totalTimeFromRouteStart,
        timestamp,
        route
      }, extraContext)
    });

    // Increment timeout counters
    this.incrementTimeoutCounters(route, isAiCopilotEnabled, referrerApp);
    this.finish({
      status: 'failure',
      failureType: 'watchdogExpired',
      duration,
      timestamp,
      totalTimeFromRouteStart,
      route
    }, checks, referrerApp, totalTimeFromRouteStart);
    this.performanceMark(`mark_all_failure_watchdog_expired`);
    this.finished = true;
  }
  getNumFailedImages() {
    return Array.from(document.getElementsByTagName('img')).reduce((total, ele) => {
      return ele.src && ele.naturalHeight === 0 && ele.naturalWidth === 0 ? total + 1 : total;
    }, 0);
  }
  finish(attrs, checks, referrerApp, routeStartTimestamp) {
    var _performanceGetEntrie;
    const hubHttpData = getHubHttpData(attrs.timestamp);
    const avgDurationBeforePreviousRhumbAborts = this.abandonedTimes.reduce((acc, duration) => {
      return acc + duration / this.abandonedTimes.length;
    }, 0);
    const reaganTiming = {};
    const reaganTimingWithPrefix = {};
    Object.keys(checks).forEach(marker => {
      if (checks[marker]) {
        const relativeTimestamp = checks[marker].timestamp - routeStartTimestamp;
        reaganTiming[marker] = relativeTimestamp;
        reaganTimingWithPrefix[`marker_timing_${marker}`] = relativeTimestamp;
        this.reportedMarkers.add(marker);
      }
    });
    this.finishedTimestamp = attrs.timestamp;
    this.finishedStatus = attrs.status;
    this.currentRoute = attrs.route;
    this.setCustomAttribute('numReaganChecksStarted', this.abandonedTimes.length + 1);
    this.setCustomAttribute('numPreviousReaganChecksAborted', this.abandonedTimes.length);
    this.setCustomAttribute('avgDurationBeforePreviousReaganAborts', avgDurationBeforePreviousRhumbAborts);
    this.setCustomAttribute('numPreviousReaganChecksFailed', 0);
    this.setCustomAttribute('numPreviousReaganChecksSuccessful', 0);
    const activationStartMs =
    // @ts-expect-error PerformanceEntry.activationStart is only available in Chromium
    ((_performanceGetEntrie = performanceGetEntriesByType('navigation')) === null || _performanceGetEntrie === void 0 || (_performanceGetEntrie = _performanceGetEntrie[0]) === null || _performanceGetEntrie === void 0 ? void 0 : _performanceGetEntrie.activationStart) || 0;
    PageLoadMetrics.counter('rhumb-finished', {
      status: attrs.status
    }).increment();
    Raven.capturePageEvent('rhumbFinished', {
      extra: Object.assign({}, attrs, {
        duration: (attrs.duration + attrs.totalTimeFromRouteStart) / 1000
      }, hubHttpData, {
        numChecksStarted: this.abandonedTimes.length + 1,
        numPreviousChecksAborted: this.abandonedTimes.length,
        avgDurationBeforePreviousRhumbAborts,
        numFailedImages: this.getNumFailedImages(),
        allVisibleMarkers: JSON.stringify(Object.keys(checks)),
        wasPrerendered: this.prerendering || activationStartMs > 0,
        wasHiddenOnScriptStart,
        wasEverHidden: this.wasHidden,
        wasHiddenWhenRhumbFinished: visibilityState() === 'hidden',
        hiddenEntirelyBeforeRhumbFinished: this.hiddenEntirelyBeforeRhumbFinished,
        hiddenWithin1000ms: this.hiddenWithin1000ms,
        hiddenWithin500ms: this.hiddenWithin500ms,
        referrerApp
      }, reaganTimingWithPrefix)
    });

    // send to Cartographer
    this.sendActions([Object.assign({
      to: {
        pathname: attrs.pathname,
        route: attrs.route,
        scenario: attrs.scenario
      },
      status: attrs.status,
      wasHidden: this.wasHidden,
      isHidden: visibilityState() === 'hidden',
      duration: (attrs.duration + attrs.totalTimeFromRouteStart) / 1000,
      // convert to seconds
      failureType: attrs.failureType,
      isReload: isReload(),
      timestamp: attrs.timestamp,
      markers: reaganTiming
    }, referrerApp ? {
      referrerApp
    } : {})], CartographerEndpoint.Navigation);
  }
  getNewMarkers(checks, routeStartTimestamp, maxTimeSinceRouteStart = Infinity) {
    const newMarkers = {};
    Object.keys(checks).forEach(marker => {
      if (checks[marker] && !this.reportedMarkers.has(marker)) {
        const relativeTimestamp = checks[marker].timestamp - routeStartTimestamp;
        if (relativeTimestamp <= maxTimeSinceRouteStart) {
          newMarkers[marker] = relativeTimestamp;
          this.reportedMarkers.add(marker);
        }
      }
    });
    return newMarkers;
  }
  reportLateMarkers(markers, routeSpec, pathname) {
    if (!this.finishedStatus) {
      Raven.captureMessage('[react-rhumb] finishedStatus must be set when reporting late markers', {
        level: 'error',
        extra: {
          route: routeSpec.route,
          pathname,
          markers
        }
      });
      return;
    }
    this.pushNavigationAction({
      to: {
        route: routeSpec.route,
        pathname
      },
      status: this.finishedStatus,
      wasHidden: this.wasHidden,
      isHidden: visibilityState() === 'hidden',
      timestamp: performance.now(),
      markers,
      isAfterRhumbFinished: true
    });
    this.flushNavigationQueue();
  }
  report(action) {
    if (action.type === 'COMPONENT_RENDERED' || !this) {
      return;
    }

    // After route finishes, only process CHECKS_CHANGED to detect late markers or ROUTE_STARTED to begin new navigation
    if (this.finished && action.type !== 'CHECKS_CHANGED' && action.type !== 'ROUTE_STARTED') {
      return;
    }
    const referrerApp = maybeGetReferrerApp();
    const {
      entry: {
        timestamp: totalTimeFromRouteStart = 0,
        checks,
        expiredTimestamp,
        pathname
      },
      routeSpec
    } = action.payload;
    switch (action.type) {
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_FAILED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_UNEXPECTED':
        {
          this.finished = true;
          break;
        }
      default:
    }
    const isAiCopilotEnabled = String(getIsAiCopilotEnabled());
    switch (action.type) {
      case 'ROUTE_STARTED':
        {
          if (this.lastAbandonedTimestamp) {
            this.abandonedTimes.push(totalTimeFromRouteStart - this.lastAbandonedTimestamp);
          }
          this.isAbandoned = false;
          this.finished = false;
          this.finishedTimestamp = null;
          this.finishedStatus = null;
          this.reportedMarkers.clear();
          this.currentRoute = routeSpec.route;
          break;
        }
      case 'ROUTE_ABANDONED':
        {
          this.lastAbandonedTimestamp = totalTimeFromRouteStart;
          this.isAbandoned = true;
          break;
        }
      case 'ROUTE_PARTIAL_SUCCESS':
        {
          const {
            partialSuccess,
            route
          } = routeSpec;
          const {
            extra: {
              scenario
            }
          } = action.payload;
          const markers = partialSuccess[scenario] || [];
          const {
            duration,
            timestamp,
            selector
          } = this.toTimings(checks, totalTimeFromRouteStart, markers);

          // Validate that success duration is under timeout threshold
          // This catches race conditions where success markers appear after timeout
          // threshold but before the delayed timeout callback fires (e.g., under high CPU load)
          if (duration + totalTimeFromRouteStart > DEFAULT_TIMEOUT) {
            this.reportTimeoutForRaceCondition(route, duration, timestamp, totalTimeFromRouteStart, checks, referrerApp, isAiCopilotEnabled, {
              scenario,
              selector,
              pathname,
              detectedInPartialSuccess: true
            });
            break;
          }
          if (!this.wasHidden) {
            PageLoadMetrics.timer('partial_success', Object.assign({
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }, referrerApp ? {
              referrerApp
            } : {})).update(totalTimeFromRouteStart + duration);
          }
          this.finish({
            status: 'partial_success',
            duration,
            timestamp,
            totalTimeFromRouteStart,
            pathname,
            scenario,
            selector,
            route
          }, checks, referrerApp, totalTimeFromRouteStart);
          this.performanceMark(`mark_all_success`);
          break;
        }
      case 'ROUTE_SUCCEEDED':
        {
          var _performanceGetEntrie2;
          const {
            success,
            route
          } = routeSpec;
          const {
            extra: {
              scenario
            }
          } = action.payload;
          if (process.env.NODE_ENV !== 'production') {
            invariant(success[scenario].length > 0, 'routeSpec for %s must have at least one `success` marker for %s', route, scenario);
          }
          const markers = success[scenario] || [];
          const {
            duration,
            timestamp,
            selector
          } = this.toTimings(checks, totalTimeFromRouteStart, markers);

          // Validate that success duration is under timeout threshold
          // This catches race conditions where success markers appear after timeout
          // threshold but before the delayed timeout callback fires (e.g., under high CPU load)
          if (duration + totalTimeFromRouteStart > DEFAULT_TIMEOUT) {
            this.reportTimeoutForRaceCondition(route, duration, timestamp, totalTimeFromRouteStart, checks, referrerApp, isAiCopilotEnabled, {
              scenario,
              selector,
              pathname,
              detectedInPartialSuccess: false
            });
            break;
          }
          if (!this.wasHidden) {
            PageLoadMetrics.timer('succeeded', Object.assign({
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }, referrerApp ? {
              referrerApp
            } : {})).update(totalTimeFromRouteStart + duration);
          }
          const activationStartMs =
          // @ts-expect-error PerformanceEntry.activationStart is only available in Chromium
          ((_performanceGetEntrie2 = performanceGetEntriesByType('navigation')) === null || _performanceGetEntrie2 === void 0 || (_performanceGetEntrie2 = _performanceGetEntrie2[0]) === null || _performanceGetEntrie2 === void 0 ? void 0 : _performanceGetEntrie2.activationStart) || 0;
          if (activationStartMs > 0) {
            PageLoadMetrics.timer('succeeded-prerender', {
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }).update(totalTimeFromRouteStart + duration - activationStartMs);
          }
          this.finish({
            status: 'success',
            duration,
            timestamp,
            totalTimeFromRouteStart,
            pathname,
            scenario,
            selector,
            route
          }, checks, referrerApp, totalTimeFromRouteStart);
          this.performanceMark(`mark_all_success`);
          break;
        }
      case 'ROUTE_FAILED':
        {
          // Avoid reporting 'ROUTE_FAILED' after 'ROUTE_ABANDONED' in case there is a race condition where failure markers
          // are still visible in the DOM after navigation away has started. E.g., hub-http can automatically redirect to
          // the login screen on 401s, and this should not be counted as a failure state even if the app briefly shows an error
          // before the redirect completes.
          if (this.isAbandoned) {
            break;
          }
          const {
            route,
            error
          } = routeSpec;
          const markers = error.filter(marker => checks[marker]);
          if (process.env.NODE_ENV !== 'production') {
            invariant(markers.length > 0, 'routeSpec for %s must have at least one `failure` marker for', route);
          }
          const {
            duration,
            timestamp,
            selector
          } = this.toTimings(checks, totalTimeFromRouteStart, markers);
          const failedMarker = markers.length ? markers[0] : '';
          if (!this.wasHidden) {
            PageLoadMetrics.counter('failed', Object.assign({
              route: cleanseRoute(route),
              selector: failedMarker,
              isAiCopilotEnabled
            }, referrerApp ? {
              referrerApp
            } : {})).increment();
          }
          this.finish({
            status: 'failure',
            failureType: 'errorSelector',
            selector,
            duration,
            timestamp,
            totalTimeFromRouteStart,
            route,
            pathname
          }, checks, referrerApp, totalTimeFromRouteStart);
          this.performanceMark(`mark_all_failure`);
          break;
        }
      case 'ROUTE_TIMEOUT_EXPIRED':
        {
          const {
            route
          } = routeSpec;
          this.incrementTimeoutCounters(route, isAiCopilotEnabled, referrerApp);
          const timeoutDuration = Math.max(0, (expiredTimestamp || 0) - totalTimeFromRouteStart);
          this.finish({
            status: 'failure',
            failureType: 'watchdogExpired',
            duration: timeoutDuration,
            timestamp: expiredTimestamp || performance.now(),
            totalTimeFromRouteStart,
            route
          }, checks, referrerApp, totalTimeFromRouteStart);
          this.performanceMark(`mark_all_failure_watchdog_expired`);
          break;
        }
      case 'CHECKS_CHANGED':
        {
          // Only report late markers for the same route that finished and within 60s of route start
          if (this.finished && this.finishedTimestamp && routeSpec.route === this.currentRoute) {
            const newMarkers = this.getNewMarkers(checks, totalTimeFromRouteStart, 60000);
            if (Object.keys(newMarkers).length > 0) {
              this.reportLateMarkers(newMarkers, routeSpec, pathname);
            }
          }
          break;
        }
      default:
    }
  }
}